import { useMemo, useRef, type ReactElement } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Spinner from "../../../../components/shared/Spinner";
import Calendar from "./Calendar";
import Grid from "./Grid";
import ActionBar from "../components/ActionBar";
import DragCopyTooltip from "../components/DragCopyTooltip";
import { useDateFilters } from "../../../../components/shared/filters/DateFilters/context/useDateFilters";
import { useScheduleContext } from "../hooks/context/useScheduleContext";
import { useIsTemplate } from "../hooks/useIsTemplate";
import { useHeightCssVariable } from "../hooks/useHeightCssVariable";

function Content({
    openActionsModal,
    openCopyModal,
    openDeleteModal,
    openAssignModal,
    openProfileSheet,
    openTransferModal,
    openShiftTradeDrawer,
    openClaimShiftDrawer,
    openSaveDayAsTemplate,
    openLoadTemplatesModal,
    openMoveToOpenShiftsModal
}: any) {
    const { mode } = useDateFilters();
    const { loading, loadingOpenShifts, loadingTemplates } = useScheduleContext();

    const content: ReactElement = useMemo(() => {
        const Component = mode === "month" ? Calendar : Grid;
        return (
            <Component
                openActionsModal={openActionsModal}
                openCopyModal={openCopyModal}
                openDeleteModal={openDeleteModal}
                openAssignModal={openAssignModal}
                openProfileSheet={openProfileSheet}
                openTransferModal={openTransferModal}
                openShiftTradeDrawer={openShiftTradeDrawer}
                openClaimShiftDrawer={openClaimShiftDrawer}
                openSaveDayAsTemplate={openSaveDayAsTemplate}
                openLoadTemplatesModal={openLoadTemplatesModal}
                openMoveToOpenShiftsModal={openMoveToOpenShiftsModal}
            />
        );
    }, [
        mode,
        openActionsModal,
        openCopyModal,
        openDeleteModal,
        openAssignModal,
        openProfileSheet,
        openTransferModal,
        openShiftTradeDrawer,
        openClaimShiftDrawer,
        openSaveDayAsTemplate,
        openLoadTemplatesModal,
        openMoveToOpenShiftsModal
    ]);

    const isTemplate = useIsTemplate();

    const isLoading = mode === "month"
        ? (loading || loadingOpenShifts)
        : (isTemplate && loadingTemplates);

    // Expose the action bar height so the team content and unassigned shifts panels shrink by it
    const actionBarRef = useRef<HTMLDivElement>(null);
    useHeightCssVariable(actionBarRef, "--action-bar-height", [isLoading]);

    return isLoading ? (
        <div className="tw-w-full tw-h-full tw-flex tw-justify-center tw-items-center align-center tw-p-20">
            <Spinner className="tw-text-primary" />
        </div>
    ) : (
        <DndProvider backend={HTML5Backend}>
            {content}
            <DragCopyTooltip />
            <div ref={actionBarRef} className="tw-flex tw-justify-center tw-w-full">
                <ActionBar
                    openActionsModal={openActionsModal}
                    openCopyModal={openCopyModal}
                    openTransferModal={openTransferModal}
                    openDeleteModal={openDeleteModal}
                />
            </div>
        </DndProvider>
    );
}

export default Content;
